import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Lock, Mail, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../../context/Auth/AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { signIn, loading: authLoading, signed } = useAuth();
  const history = useHistory();

  useEffect(() => {
    if (signed) {
      history.replace("/dashboard");
    }
  }, [signed, history]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#191c1e] flex flex-col items-center justify-center p-4">
        <Loader2 className="animate-spin text-purple-400 mb-3" size={36} />
        <span className="text-sm font-semibold text-gray-400">Carregando sessão...</span>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signIn(email, password);
      history.replace("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.error || err?.response?.data?.message;

      if (!err.response) {
        setError("Não foi possível conectar à API. Verifique rede e CORS.");
      } else if (status === 401) {
        setError(backendMessage || "E-mail ou senha inválidos.");
      } else if (status === 403) {
        setError(backendMessage || "Usuário sem permissão de superadministrador.");
      } else {
        setError(backendMessage || `Falha na autenticação (HTTP ${status || "desconhecido"}).`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#191c1e] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient background glows */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-pink-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="neumorphic-card rounded-3xl p-8 sm:p-10 border border-white/10 shadow-2xl backdrop-blur-xl">
          {/* Brand Logo & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl create-event-btn text-white mb-4 shadow-purple-glow">
              <Sparkles size={32} />
            </div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">
              TicketWhats <span className="ticket-whats-logo">Admin</span>
            </h1>
            <p className="text-xs text-gray-400 mt-1.5 font-medium">
              Portal Administrativo da Plataforma SaaS
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-semibold flex items-start gap-3">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">
                E-mail Administrativo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-500">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  required
                  disabled={loading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ticketwhats.com"
                  className="w-full pl-10 pr-4 py-3 bg-[#191c1e] border border-white/10 rounded-xl text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500/80 focus:ring-1 focus:ring-purple-500/80 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-500">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  required
                  disabled={loading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-[#191c1e] border border-white/10 rounded-xl text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500/80 focus:ring-1 focus:ring-purple-500/80 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-6 create-event-btn text-white font-bold text-sm rounded-xl shadow-lg transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Entrando...
                </>
              ) : (
                "Acessar Painel"
              )}
            </button>
          </form>

          <div className="mt-8 text-center text-xs text-gray-500 border-t border-white/5 pt-4">
            Acesso restrito para superadministradores TicketWhats.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
